
import { supabase } from '@/integrations/supabase/client';
import { Profile } from '@/types/auth';

// Cache simple pour éviter les appels répétés
const profileCache = new Map<string, { profile: Profile; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const profileService = {
  async loadUserProfile(userId: string, userEmail?: string): Promise<Profile> {
    console.log('🔍 [ProfileService] Loading profile for:', userId);
    
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      console.error('❌ [ProfileService] Error loading profile:', error);
      
      if (error.code === 'PGRST116') {
        console.log('📝 [ProfileService] Profile not found, creating new profile');
        
        const newProfile = {
          id: userId,
          email: userEmail || '',
          name: userEmail?.split('@')[0] || 'Utilisateur',
          role: 'ouvrier' as const,
          avatar_url: null,
          phone: null,
          pin: '0000',
          active: true
        };

        const { data: createdProfile, error: createError } = await supabase
          .from('profiles')
          .insert(newProfile)
          .select()
          .single();

        if (createError) {
          console.error('❌ [ProfileService] Error creating profile:', createError);
          throw createError;
        }

        console.log('✅ [ProfileService] Profile created:', createdProfile.role);
        
        const typedProfile: Profile = {
          id: createdProfile.id,
          name: createdProfile.name,
          email: createdProfile.email,
          nickname: createdProfile.email.split('@')[0],
          role: createdProfile.role as "admin" | "ouvrier",
          avatar_url: createdProfile.avatar_url,
          phone: createdProfile.phone,
          pin: createdProfile.pin,
          active: createdProfile.active
        };
        
        // Mettre en cache
        profileCache.set(userId, { 
          profile: typedProfile, 
          timestamp: Date.now() 
        });
        
        return typedProfile;
      }
      
      throw error;
    }

    console.log('✅ [ProfileService] Profile loaded:', profile.role);
    
    const typedProfile: Profile = {
      id: profile.id,
      name: profile.name,
      email: profile.email,
      nickname: profile.email.split('@')[0],
      role: profile.role as "admin" | "ouvrier",
      avatar_url: profile.avatar_url,
      phone: profile.phone,
      pin: profile.pin,
      active: profile.active
    };
    
    // Mettre en cache
    profileCache.set(userId, { 
      profile: typedProfile, 
      timestamp: Date.now() 
    });
    
    return typedProfile;
  },

  clearCache(userId?: string) {
    console.log('🧹 [ProfileService] Clearing cache', userId ? `for user: ${userId}` : 'for all users');
    if (userId) {
      profileCache.delete(userId);
    } else {
      profileCache.clear();
    }
  }
};
